import React, {useState} from "react";
import {Button, Tag, Space, Tooltip} from "antd";
import {CheckOutlined, DownloadOutlined} from "@ant-design/icons";
import {useObserver} from "mobx-react-lite";
import moment from "moment";
import {useStore} from "hooks/useStore";
import {ExpandableStyledTable} from "components/_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";
import {ResponsiveDataTableStyled} from "components/_defaultComponents/ResponsiveDataTable";

const ResponsiveDataTable = ({loading, data, dataCount, currentPage, setCurrentPage, recall}) => {
    const stores = useStore();
    const [loadingApprove, setLoadingApprove] = useState(false);
    const [loadingDownloadLabel, setLoadingDownloadLabel] = useState(false);
    const handleApprove = async id => {
        setLoadingApprove(true);
        try {
            const result = await stores.equipmentRequestStore.approveRequest({id});
            if (result) {
                recall();
            }
        } catch (error) {
        } finally {
            setLoadingApprove(false);
        }
    };

    const handleDownloadLabel = async id => {
        setLoadingDownloadLabel(true);
        try {
            const result = await stores.equipmentRequestStore.downloadLabel({id});
            if (result) {
                recall();
            }
        } catch (error) {
        } finally {
            setLoadingDownloadLabel(false);
        }
    };

    const expandedRowRender = record => {
        const columns = [
            {
                title: "Ekipman Türü",
                dataIndex: "equipmentName",
                key: "equipmentName",
                onCell: () => ({
                    "data-label": "Ekipman Türü"
                })
            },
            {
                title: "Beden",
                dataIndex: "variationSize",
                key: "variationSize",
                render: text => text || "-",
                onCell: () => ({
                    "data-label": "Beden"
                })
            },
            {
                title: "Renk",
                dataIndex: "variationColor",
                key: "variationColor",
                render: text => text || "-",
                onCell: () => ({
                    "data-label": "Renk"
                })
            },
            {
                title: "Adet",
                dataIndex: "quantity",
                key: "quantity",
                onCell: () => ({
                    "data-label": "Adet"
                })
            }
        ];

        return (
            <ResponsiveDataTableStyled
                columns={columns}
                dataSource={record.requestedEquipments}
                pagination={false}
                rowKey={item => item.equipmentId + item.variationId}
                size="small"
            />
        );
    };

    const canSeeActions = stores.meStore.checkRole("canSeeEquipmentRequestActions");

    const columns = [
        {
            title: "Tarih",
            dataIndex: "createdAt",
            key: "createdAt",
            render: text => moment(text).format("DD/MM/YYYY HH:mm"),
            width: 150,
            onCell: () => ({
                "data-label": "Tarih"
            })
        },
        {
            title: "Talep Eden Kişi",
            dataIndex: "requesterName",
            key: "requesterName",
            onCell: () => ({
                "data-label": "Talep Eden Kişi"
            })
        },
        {
            title: "Telefon",
            dataIndex: "requesterPhone",
            key: "requesterPhone",
            onCell: () => ({
                "data-label": "Telefon"
            })
        },
        {
            title: "Bölge Müdürlüğü",
            dataIndex: ["zoneManagement", "name"],
            key: "zoneManagementName",
            onCell: () => ({
                "data-label": "Bölge Müdürlüğü"
            })
        },
        {
            title: "Bölge",
            dataIndex: ["zone", "name"],
            key: "zoneName",
            onCell: () => ({
                "data-label": "Bölge"
            })
        },
        {
            title: "Durum",
            dataIndex: "status",
            key: "status",
            render: status => {
                const statusConfig = {
                    pending: {color: "orange", text: "Bekliyor"},
                    approved: {color: "green", text: "Onaylandı"}
                };
                const config = statusConfig[status] || {color: "default", text: status};
                return <Tag color={config.color}>{config.text}</Tag>;
            },
            width: 120,
            onCell: () => ({
                "data-label": "Durum"
            })
        },
        ...(canSeeActions
            ? [
                  {
                      title: "Aksiyon",
                      key: "action",
                      render: (_, record) => (
                          <Space size="small">
                              <Tooltip
                                  title={record.status === "approved" ? "Onaylı" : "Talebi onayla"}
                              >
                                  <Button
                                      type="primary"
                                      style={{
                                          backgroundColor:
                                              record.status === "approved" ? "green" : null,
                                          borderColor:
                                              record.status === "approved" ? "green" : null,
                                          color: record.status === "approved" ? "white" : null,
                                          opacity: record.status === "approved" ? 1 : null
                                      }}
                                      showTooltip={false}
                                      icon={<CheckOutlined />}
                                      shape="circle"
                                      onClick={() => handleApprove(record._id)}
                                      disabled={record.status === "approved" || loadingApprove}
                                      loading={loadingApprove}
                                  />
                              </Tooltip>

                              <Tooltip
                                  title={
                                      record.status !== "approved"
                                          ? "Sadece onaylanmış talepleri çıktı alabilirsiniz"
                                          : ""
                                  }
                              >
                                  <Button
                                      type="default"
                                      icon={<DownloadOutlined />}
                                      onClick={() => handleDownloadLabel(record._id)}
                                      disabled={
                                          record.status !== "approved" || loadingDownloadLabel
                                      }
                                      loading={loadingDownloadLabel}
                                  />
                              </Tooltip>
                          </Space>
                      ),
                      width: 150,
                      onCell: () => ({
                          "data-label": "Aksiyon"
                      })
                  }
              ]
            : [])
    ];

    return useObserver(() => (
        <ExpandableStyledTable
            size="small"
            style={{width: "100%"}}
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey={record => record._id}
            expandable={{
                expandedRowRender,
                rowExpandable: record => record.requestedEquipments?.length > 0
            }}
            pagination={{
                current: currentPage,
                pageSize: 10,
                total: dataCount,
                onChange: page => setCurrentPage(page),
                showSizeChanger: false,
                showTotal: total => `Toplam ${total} kayıt`
            }}
            scroll={{x: 1200}}
        />
    ));
};

export default ResponsiveDataTable;
